/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { QuantityUpdater } from './OrderItemFulfillmentIssueBar/QuantityUpdater'
import { Button, ButtonGroup, Container, Message } from 'semantic-ui-react'
import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import { useSaveFulfillment } from './useSaveFulfillment'
import { convertOrderItemsToFulfillmentIssues } from '../../../../../services/Order/Order.service'

const ADJUSTMENT_MODES = {
    MULTIPLE_PIECES: 'MULTIPLE_PIECES',
    TOTAL_WEIGHT: 'TOTAL_WEIGHT',
}

const AdjustWeight = ({orderId, pickingMethod, orderItem, onSubmit}) => {
    const [t] = useTranslation()
    const [focusIndex, setFocusIndex] = useState(0)

    const initialAdjustmentMode =
      orderItem.item.pricedByUnit?.type !== 'COUNT' &&
      orderItem.sellableUnit.type === 'COUNT' ?
        ADJUSTMENT_MODES.MULTIPLE_PIECES :
        ADJUSTMENT_MODES.TOTAL_WEIGHT

    const [adjustmentMode, setAdjustmentMode] = useState(initialAdjustmentMode)
    const sellingOption = orderItem.item.sellingOptions.find(so => so.measurementUnit.id === orderItem.sellableUnit.id)
    const conversionRate = sellingOption ? sellingOption.conversionRate : 1
    const initialMap = new Map(
      Array.from({length: orderItem.quantity}, (_, index) => [index + 1, conversionRate]),
    )

    const [adjustedQuantities, setAdjustedQuantities] = useState(initialMap)
    const {saveFulfillmentIssuesMutation, foundItemMutation} = useSaveFulfillment(+orderId, orderItem.id)

    const handleQuantityChange = (newQuantity) => {
        const isPricedByWeightOrVolumeAndSoldByCount =
          orderItem.item.pricedByUnit?.type !== 'COUNT' &&
          orderItem.sellableUnit?.type === 'COUNT'

        let quantityToCompare = newQuantity

        if (isPricedByWeightOrVolumeAndSoldByCount) {
            const sellingOption = orderItem.item.sellingOptions.find(so => so.measurementUnit.type === orderItem.sellableUnit.type)

            if (!sellingOption) {
                return
            }

            // Number of unit of price by weight or volume to compare with the initial quantity
            quantityToCompare = Math.round(newQuantity / sellingOption.conversionRate)
        }

        if (quantityToCompare < orderItem.initialQuantity) {
            saveFulfillmentIssuesMutation.mutate({
                fulfillmentIssues: convertOrderItemsToFulfillmentIssues(
                  [{...orderItem, quantity: newQuantity}],
                  [],
                ),
            })
        }

        if (quantityToCompare >= orderItem.initialQuantity) {
            foundItemMutation.mutate({
                orderItem: {
                    ...orderItem,
                    quantity: newQuantity,
                },
                pickingMethod: pickingMethod || 'MANUAL',
            })
        }
    }

    return (
      <Container>
          <div
            css={css`
                display: flex;
                justify-content: center;
            `}
          >
              <ButtonGroup>
                  <Button
                    color="teal"
                    basic={adjustmentMode !== ADJUSTMENT_MODES.TOTAL_WEIGHT}
                    onClick={() => setAdjustmentMode(ADJUSTMENT_MODES.TOTAL_WEIGHT)}
                  >
                      {t('order.pickup.adjust_weight.adjustment_mode.TOTAL_WEIGHT')}
                  </Button>
                  <Button
                    color="teal"
                    basic={adjustmentMode !== ADJUSTMENT_MODES.MULTIPLE_PIECES}
                    disabled={sellingOption.measurementUnit.type !== 'COUNT'}
                    onClick={() => setAdjustmentMode(ADJUSTMENT_MODES.MULTIPLE_PIECES)}
                  >
                      {t('order.pickup.adjust_weight.adjustment_mode.MULTIPLE_PIECES')}
                  </Button>
              </ButtonGroup>
          </div>
          <Message
            css={css`
                &.ui.message {
                    text-align: center;
                }
            `}
            header={adjustmentMode === ADJUSTMENT_MODES.TOTAL_WEIGHT ?
              t('order.pickup.adjust_weight.header_weight') :
              t('order.pickup.adjust_weight.header_piece')
            }
            content={orderItem.item.object.name}
          />

          <div css={css`
              display: flex;
              flex-direction: column;
              row-gap: 1rem;
          `}>
              <div css={css`
                  display: flex;
                  flex-direction: column;
                  row-gap: 1rem;
                  align-items: center;
              `}>
                  {adjustmentMode === ADJUSTMENT_MODES.TOTAL_WEIGHT && (

                    <QuantityUpdater
                      estimatedCount={orderItem.initialQuantity}
                      orderId={orderId}
                      orderItem={orderItem}
                      autofocus
                      onSubmit={(value) => {
                          handleQuantityChange(value)
                          if (onSubmit) onSubmit()
                      }}
                      pickingMethod={pickingMethod}
                    />
                  )}

                  {adjustmentMode === ADJUSTMENT_MODES.MULTIPLE_PIECES && (
                    Array.from({length: orderItem.quantity}).map((_, index) => (
                        <div
                          key={index}
                          css={css`
                              display: flex;
                              column-gap: 1rem;
                              align-items: center;
                          `}
                        >
                            {t('order.pickup.adjust_weight.piece_number', {count: index + 1})} :
                            <QuantityUpdater
                              key={index}
                              estimatedCount={1}
                              orderId={orderId}
                              orderItem={orderItem}
                              autofocus={index === focusIndex}
                              onSubmit={(value) => {
                                  const newAdjustedQuantities = new Map(adjustedQuantities)
                                  newAdjustedQuantities.set(index + 1, value)
                                  setAdjustedQuantities(newAdjustedQuantities)

                                  if (index < orderItem.quantity - 1) {
                                      setFocusIndex(index + 1)
                                  } else {
                                      const totalAdjustedSum = Array.from(newAdjustedQuantities.values()).reduce((sum, value) => sum + value, 0)
                                      handleQuantityChange(totalAdjustedSum)
                                      if (onSubmit) onSubmit()
                                  }
                              }}
                              pickingMethod={pickingMethod}
                            />
                        </div>
                      ),
                    ))}
              </div>
          </div>
      </Container>
    )
}

export { AdjustWeight }
